// src/pages/restaurant-tables/CategoryList.jsx
import React, { useState, useEffect, useCallback, useMemo, lazy, Suspense } from 'react';
import {
  Box,
  Typography,
  Grid,
  Button,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  FormHelperText,
  LinearProgress,
  Alert,
  Snackbar,
  TablePagination,
  InputAdornment,
  CircularProgress,
  Fab,
  Divider,
  Fade,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Card,
  CardContent,
  useTheme,
  alpha,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Category as CategoryIcon,
  RestaurantMenu as MenuIcon,
  CalendarToday as CalendarIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  Refresh as RefreshIcon,
  Cancel as CancelIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
} from '@mui/icons-material';

// Import theme colors
import { colors } from '../../../theme/colors';

// Lazy load components
const AfricanSidebar = lazy(() => import('../../../components/RestaurantPOS/manager/african/Sidebar'));
const ContinentalSidebar = lazy(() => import('../../../components/RestaurantPOS/manager/continental/Sidebar'));
const LoungeSidebar = lazy(() => import('../../../components/RestaurantPOS/manager/lounge/Sidebar'));
const Header = lazy(() => import('../../../components/RestaurantPOS/manager/Header'));
const Footer = lazy(() => import('../../../components/RestaurantPOS/manager/Footer'));
const Preloader = lazy(() => import('../../../theme/Preloader'));

// API Configuration
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
const API_PREFIX = process.env.REACT_APP_API_PREFIX || '/pos_v1';
const FULL_API_URL = `${API_BASE_URL}${API_PREFIX}`;

// ============================================
// TOKEN UTILITY FUNCTIONS
// ============================================

const isTokenExpired = (token) => {
  try {
    if (!token) return true;
    const payload = token.split('.')[1];
    if (!payload) return true;
    const decoded = JSON.parse(atob(payload));
    if (!decoded.exp) return true;
    return decoded.exp * 1000 < Date.now();
  } catch (err) {
    return true;
  }
};

const getTokenExpiryTime = (token) => {
  try {
    if (!token) return 0;
    const payload = token.split('.')[1];
    if (!payload) return 0;
    const decoded = JSON.parse(atob(payload));
    return decoded.exp ? decoded.exp * 1000 : 0;
  } catch (err) {
    return 0;
  }
};

// ============================================
// UTILITY FUNCTIONS & CONSTANTS
// ============================================

// Location configurations
const LOCATION_CONFIG = {
  african: {
    id: 5,
    name: 'African',
    color: '#2E7D32',
    lightColor: '#4CAF50',
    icon: '🌍',
    sidebar: AfricanSidebar,
    path: '/african-categories'
  },
  continental: {
    id: 3,
    name: 'Continental',
    color: '#1565C0',
    lightColor: '#1976D2',
    icon: '🌐',
    sidebar: ContinentalSidebar,
    path: '/continental-categories'
  },
  lounge: {
    id: 4,
    name: 'Lounge',
    color: '#6A1B9A',
    lightColor: '#9C27B0',
    icon: '🍸',
    sidebar: LoungeSidebar,
    path: '/lounge-categories'
  }
};

// ============================================
// SUB-COMPONENTS FOR BETTER PERFORMANCE
// ============================================

// Memoized Stats Card Component
const StatsCard = React.memo(({ stats, locationColor }) => {
  const theme = useTheme();
  
  const statItems = [
    { label: 'Total Categories', value: stats.total, color: locationColor },
    { label: 'Active', value: stats.active, color: '#4caf50' },
    { label: 'Inactive', value: stats.inactive, color: '#f44336' },
  ];

  return (
    <Grid container spacing={2} sx={{ mb: 3 }}>
      {statItems.map((item) => (
        <Grid item xs={12} sm={6} md={4} key={item.label}>
          <Card
            sx={{
              borderRadius: 2,
              borderLeft: `4px solid ${item.color}`,
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              transition: 'transform 0.2s ease, box-shadow 0.2s ease',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
              },
            }}
          >
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: 0.5 }}>
                {item.label}
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 700, color: item.color }}>
                {item.value}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
});

// Memoized Category Table Row Component
const CategoryRow = React.memo(({ 
  category, 
  onEdit, 
  onDelete, 
  onToggle, 
  onView, 
  index 
}) => {
  const theme = useTheme();
  const statusColor = category.is_active ? '#4caf50' : '#f44336';
  const statusLabel = category.is_active ? 'Active' : 'Inactive';

  return (
    <TableRow
      sx={{
        '&:hover': {
          backgroundColor: alpha(theme.palette.primary.main, 0.04),
        },
        transition: 'background-color 0.2s ease',
      }}
    >
      <TableCell>{index + 1}</TableCell>
      <TableCell>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CategoryIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
          <Typography variant="body2" fontWeight={500}>
            {category.name}
          </Typography>
        </Box>
      </TableCell>
      <TableCell align="center">
        <Chip
          label={category.item_count || 0}
          size="small"
          sx={{
            fontWeight: 600,
            backgroundColor: alpha(theme.palette.primary.main, 0.1),
            color: theme.palette.primary.main,
          }}
        />
      </TableCell>
      <TableCell>
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
          {category.created_date ? new Date(category.created_date).toLocaleDateString() : 'N/A'}
        </Typography>
      </TableCell>
      <TableCell>
        <Chip
          label={statusLabel}
          size="small"
          sx={{
            backgroundColor: alpha(statusColor, 0.1),
            color: statusColor,
            fontWeight: 600,
            fontSize: '0.7rem',
          }}
        />
      </TableCell>
      <TableCell align="center">
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
          <Tooltip title="View Details">
            <IconButton
              size="small"
              onClick={() => onView('view', category)}
              sx={{ color: theme.palette.info.main }}
            >
              <ViewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit">
            <IconButton
              size="small"
              onClick={() => onEdit('edit', category)}
              sx={{ color: theme.palette.primary.main }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={category.is_active ? 'Deactivate' : 'Activate'}>
            <IconButton
              size="small"
              onClick={() => onToggle(category)}
              sx={{ 
                color: category.is_active ? theme.palette.warning.main : theme.palette.success.main 
              }}
            >
              {category.is_active ? <CancelIcon fontSize="small" /> : <CheckCircleIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
          <Tooltip title="Permanently Delete">
            <IconButton
              size="small"
              onClick={() => onDelete(category)}
              sx={{ color: theme.palette.error.main }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </TableCell>
    </TableRow>
  );
});

// ============================================
// MAIN COMPONENT
// ============================================

const CategoryList = ({ locationKey = 'african' }) => {
  const theme = useTheme();
  
  // ============================================
  // STATE MANAGEMENT
  // ============================================

  const [categories, setCategories] = useState([]);
  const [filteredCategories, setFilteredCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeMenu, setActiveMenu] = useState('Menu');
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Stats state
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    inactive: 0,
  });

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [orderBy, setOrderBy] = useState('name');
  const [order, setOrder] = useState('asc');

  // Dialog states
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState('create');
  const [selectedCategory, setSelectedCategory] = useState(null);

  // Form state
  const defaultFormData = useMemo(() => ({
    name: '',
    description: '',
    is_active: true,
    location_id: '',
    created_date: new Date().toISOString().split('T')[0],
  }), []);

  const [formData, setFormData] = useState(defaultFormData);
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // Toggle (Activate/Deactivate) dialog state
  const [toggleDialogOpen, setToggleDialogOpen] = useState(false);
  const [categoryToToggle, setCategoryToToggle] = useState(null);
  const [toggling, setToggling] = useState(false);

  // Delete (Permanent) dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');

  // Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  // Get location config
  const locationConfig = useMemo(() => 
    LOCATION_CONFIG[locationKey] || LOCATION_CONFIG.african,
    [locationKey]
  );
  
  const { name: locationName, id: locationIdValue, color: locationColor } = locationConfig;
  const SidebarComponent = locationConfig.sidebar;

  // ============================================
  // TOKEN MANAGEMENT FUNCTIONS
  // ============================================

  const getAuthToken = useCallback(() => {
    try {
      const token = localStorage.getItem('access_token');
      if (!token || isTokenExpired(token)) {
        console.warn('[CategoryList] Token invalid or expired');
        return null;
      }
      return token;
    } catch (err) {
      console.error('[CategoryList] Error getting auth token:', err);
      return null;
    }
  }, []);

  const refreshAuthToken = useCallback(async () => {
    try {
      setIsRefreshing(true);
      const refreshToken = localStorage.getItem('refresh_token');
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }

      const response = await fetch(`${FULL_API_URL}/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });

      if (!response.ok) {
        throw new Error('Refresh failed');
      }

      const data = await response.json();
      if (data.access_token) {
        localStorage.setItem('access_token', data.access_token);
        return data.access_token;
      }
      throw new Error('No access token in refresh response');
    } catch (err) {
      console.error('[CategoryList] Token refresh failed:', err);
      // Clear all auth data and redirect to login
      localStorage.clear();
      window.location.href = '/restaurantAuth';
      return null;
    } finally {
      setIsRefreshing(false);
    }
  }, []);

  // ============================================
  // API CALLS - Enhanced with token refresh
  // ============================================

  const apiRequest = useCallback(async (endpoint, options = {}) => {
    let token = getAuthToken();
    
    // If token is invalid, try to refresh
    if (!token) {
      console.log('[CategoryList] Token invalid, attempting refresh...');
      token = await refreshAuthToken();
      if (!token) {
        throw new Error('Authentication required');
      }
    }

    const headers = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const url = `${FULL_API_URL}${endpoint}`;
    console.debug(`[CategoryList] API Request: ${options.method || 'GET'} ${url}`);

    try {
      const response = await fetch(url, {
        ...options,
        headers,
      });

      // If 401, try refreshing once
      if (response.status === 401) {
        console.log('[CategoryList] Received 401, attempting token refresh...');
        const newToken = await refreshAuthToken();
        if (newToken) {
          // Retry the request with new token
          headers['Authorization'] = `Bearer ${newToken}`;
          const retryResponse = await fetch(url, {
            ...options,
            headers,
          });
          if (retryResponse.ok) {
            const contentType = retryResponse.headers.get('content-type');
            if (contentType && contentType.includes('application/json')) {
              return await retryResponse.json();
            }
            return { message: await retryResponse.text() };
          }
          // If retry fails with 401, throw error
          if (retryResponse.status === 401) {
            throw new Error('Authentication failed after refresh');
          }
        }
        throw new Error('Authentication failed');
      }

      const contentType = response.headers.get('content-type');
      let data;
      if (contentType && contentType.includes('application/json')) {
        data = await response.json();
      } else {
        const text = await response.text();
        data = { message: text || 'Non-JSON response received' };
      }

      if (!response.ok) {
        const errorMessage = data.detail || data.message || `HTTP ${response.status}`;
        throw new Error(errorMessage);
      }

      return data;
    } catch (err) {
      console.error('[CategoryList] API Error:', err);
      throw err;
    }
  }, [getAuthToken, refreshAuthToken]);

  // ============================================
  // FETCH FUNCTIONS - Enhanced with retry logic
  // ============================================

  const fetchCategories = useCallback(async () => {
    setLoading(true);
    try {
      console.debug(`[CategoryList] Fetching categories for ${locationName} location...`);
      
      const data = await apiRequest(`/categories/location/${locationKey}`);
      console.debug(`[CategoryList] Categories fetched:`, data);
      
      const categoriesData = Array.isArray(data) ? data : [];
      setCategories(categoriesData);
      setFilteredCategories(categoriesData);
      
      // Calculate stats
      const active = categoriesData.filter(c => c.is_active).length;
      setStats({
        total: categoriesData.length,
        active: active,
        inactive: categoriesData.length - active,
      });
      
    } catch (err) {
      console.error(`[CategoryList] Error fetching categories:`, err);
      
      // Check if error is authentication related
      if (err.message === 'Authentication required' || err.message === 'Authentication failed after refresh') {
        setSnackbar({
          open: true,
          message: 'Session expired. Please login again.',
          severity: 'error'
        });
        setTimeout(() => {
          localStorage.clear();
          window.location.href = '/restaurantAuth';
        }, 2000);
      } else {
        setSnackbar({
          open: true,
          message: `Failed to fetch categories: ${err.message}`,
          severity: 'error',
        });
      }
    } finally {
      setLoading(false);
      setIsLoading(false);
    }
  }, [apiRequest, locationKey, locationName]);

  // ============================================
  // CRUD OPERATIONS - Enhanced with error handling
  // ============================================

  const handleCreateCategory = useCallback(async (data) => {
    try {
      const submitData = {
        ...data,
        location_id: locationIdValue,
      };
      
      console.debug(`[CategoryList] Creating category:`, submitData);
      const result = await apiRequest('/categories/', {
        method: 'POST',
        body: JSON.stringify(submitData),
      });
      
      setSnackbar({
        open: true,
        message: `Category "${result.name}" created successfully!`,
        severity: 'success',
      });
      
      await fetchCategories();
      handleCloseDialog();
      return result;
    } catch (err) {
      console.error(`[CategoryList] Error creating category:`, err);
      
      const errorMessage = err.message || '';
      let userFriendlyMessage = `Failed to create category.`;
      
      if (errorMessage.toLowerCase().includes('already exists') || 
          errorMessage.toLowerCase().includes('duplicate entry')) {
        userFriendlyMessage = `A category named "${data.name}" already exists. Please use a different name.`;
      } else if (errorMessage === 'Authentication required' || errorMessage === 'Authentication failed after refresh') {
        userFriendlyMessage = 'Session expired. Please login again.';
        setTimeout(() => {
          localStorage.clear();
          window.location.href = '/restaurantAuth';
        }, 2000);
      } else {
        userFriendlyMessage = `Failed to create category: ${errorMessage}`;
      }
      
      setSnackbar({
        open: true,
        message: userFriendlyMessage,
        severity: 'error',
      });
      
      throw err;
    }
  }, [apiRequest, fetchCategories, locationIdValue]);

  const handleUpdateCategory = useCallback(async (id, data) => {
    try {
      const submitData = {
        ...data,
        location_id: locationIdValue,
      };
      
      console.debug(`[CategoryList] Updating category ${id}:`, submitData);
      const result = await apiRequest(`/categories/${id}`, {
        method: 'PUT',
        body: JSON.stringify(submitData),
      });
      
      setSnackbar({
        open: true,
        message: `Category "${result.name}" updated successfully!`,
        severity: 'success',
      });
      
      await fetchCategories();
      handleCloseDialog();
      return result;
    } catch (err) {
      console.error(`[CategoryList] Error updating category:`, err);
      
      const errorMessage = err.message || '';
      let userFriendlyMessage = `Failed to update category.`;
      
      if (errorMessage.toLowerCase().includes('already exists') || 
          errorMessage.toLowerCase().includes('duplicate entry')) {
        userFriendlyMessage = `A category named "${data.name}" already exists. Please use a different name.`;
      } else if (errorMessage === 'Authentication required' || errorMessage === 'Authentication failed after refresh') {
        userFriendlyMessage = 'Session expired. Please login again.';
        setTimeout(() => {
          localStorage.clear();
          window.location.href = '/restaurantAuth';
        }, 2000);
      } else {
        userFriendlyMessage = `Failed to update category: ${errorMessage}`;
      }
      
      setSnackbar({
        open: true,
        message: userFriendlyMessage,
        severity: 'error',
      });
      
      throw err;
    }
  }, [apiRequest, fetchCategories, locationIdValue]);

  const handleToggleCategoryStatus = useCallback(async (id, currentStatus) => {
    setToggling(true);
    try {
      const newStatus = !currentStatus;
      console.debug(`[CategoryList] Toggling category ${id} status to ${newStatus}`);
      
      const result = await apiRequest(`/categories/${id}/toggle-status`, {
        method: 'PATCH',
        body: JSON.stringify({ is_active: newStatus }),
      });
      
      setSnackbar({
        open: true,
        message: `Category "${result.name}" ${newStatus ? 'activated' : 'deactivated'} successfully!`,
        severity: 'success',
      });
      
      await fetchCategories();
      handleCloseToggleDialog();
    } catch (err) {
      console.error(`[CategoryList] Error toggling category status:`, err);
      
      const errorMessage = err.message || '';
      let userFriendlyMessage = `Failed to toggle category status: ${errorMessage}`;
      
      if (errorMessage === 'Authentication required' || errorMessage === 'Authentication failed after refresh') {
        userFriendlyMessage = 'Session expired. Please login again.';
        setTimeout(() => {
          localStorage.clear();
          window.location.href = '/restaurantAuth';
        }, 2000);
      }
      
      setSnackbar({
        open: true,
        message: userFriendlyMessage,
        severity: 'error',
      });
    } finally {
      setToggling(false);
    }
  }, [apiRequest, fetchCategories]);

  const handlePermanentDeleteCategory = useCallback(async (id) => {
    setDeleting(true);
    try {
      console.debug(`[CategoryList] Permanently deleting category ${id}`);
      
      const result = await apiRequest(`/categories/${id}/permanent`, {
        method: 'DELETE',
      });
      
      setSnackbar({
        open: true,
        message: result.message || `Category permanently deleted successfully!`,
        severity: 'success',
      });
      
      await fetchCategories();
      handleCloseDeleteDialog();
    } catch (err) {
      console.error(`[CategoryList] Error permanently deleting category:`, err);
      
      const errorMessage = err.message || '';
      let userFriendlyMessage = `Failed to delete category: ${errorMessage}`;
      
      if (errorMessage === 'Authentication required' || errorMessage === 'Authentication failed after refresh') {
        userFriendlyMessage = 'Session expired. Please login again.';
        setTimeout(() => {
          localStorage.clear();
          window.location.href = '/restaurantAuth';
        }, 2000);
      }
      
      setSnackbar({
        open: true,
        message: userFriendlyMessage,
        severity: 'error',
      });
    } finally {
      setDeleting(false);
    }
  }, [apiRequest, fetchCategories]);

  // ============================================
  // DIALOG HANDLERS
  // ============================================

  const handleOpenDialog = useCallback((mode, category = null) => {
    console.debug(`[CategoryList] Opening dialog in ${mode} mode:`, category);
    
    const today = new Date().toISOString().split('T')[0];
    
    // Set dialog mode and selected category
    setDialogMode(mode);
    setSelectedCategory(category);
    setFormErrors({});
    setSubmitting(false);
    
    // Prepare form data based on mode
    let newFormData;
    if (mode === 'create') {
      newFormData = {
        name: '',
        description: '',
        is_active: true,
        location_id: locationIdValue,
        created_date: today,
      };
    } else if (category) {
      // For edit and view modes, populate with category data
      newFormData = {
        name: category.name || '',
        description: category.description || '',
        is_active: category.is_active !== undefined ? category.is_active : true,
        location_id: locationIdValue,
        created_date: category.created_date || today,
      };
    } else {
      newFormData = defaultFormData;
    }
    
    setFormData(newFormData);
    setDialogOpen(true);
  }, [locationIdValue, defaultFormData]);

  const handleCloseDialog = useCallback(() => {
    setDialogOpen(false);
    setSelectedCategory(null);
    setFormErrors({});
    setSubmitting(false);
    setFormData(defaultFormData);
  }, [defaultFormData]);

  const handleOpenToggleDialog = useCallback((category) => {
    console.debug('[CategoryList] Opening toggle dialog for:', category);
    setCategoryToToggle(category);
    setToggleDialogOpen(true);
  }, []);

  const handleCloseToggleDialog = useCallback(() => {
    setToggleDialogOpen(false);
    setCategoryToToggle(null);
    setToggling(false);
  }, []);

  const handleOpenDeleteDialog = useCallback((category) => {
    console.debug('[CategoryList] Opening delete dialog for:', category);
    setCategoryToDelete(category);
    setDeleteConfirmation('');
    setDeleteDialogOpen(true);
  }, []);

  const handleCloseDeleteDialog = useCallback(() => {
    setDeleteDialogOpen(false);
    setCategoryToDelete(null);
    setDeleteConfirmation('');
    setDeleting(false);
  }, []);

  // ============================================
  // FORM HANDLERS
  // ============================================

  const handleFormChange = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: '' }));
    }
  }, [formErrors]);

  const validateForm = useCallback(() => {
    const errors = {};
    const trimmedName = formData?.name?.trim() || '';
    
    if (!trimmedName) {
      errors.name = 'Category name is required';
    } else if (trimmedName.length < 2) {
      errors.name = 'Category name must be at least 2 characters';
    } else if (trimmedName.length > 50) {
      errors.name = 'Category name must be 50 characters or less';
    } else if (!/^[a-zA-Z0-9\s\-_&']+$/.test(trimmedName)) {
      errors.name = 'Category name contains invalid characters';
    }
    
    // Check for duplicates (exclude current category when editing)
    const existing = categories.find(c => 
      c.name?.toLowerCase() === trimmedName.toLowerCase() && 
      c.id !== selectedCategory?.id
    );
    if (existing) {
      errors.name = `"${trimmedName}" already exists. Please use a different name.`;
    }
    
    if (!formData?.created_date) {
      errors.created_date = 'Created date is required';
    }
    
    setFormErrors(errors);
    console.debug('[CategoryList] Validation result:', Object.keys(errors).length === 0 ? 'Valid' : errors);
    return Object.keys(errors).length === 0;
  }, [formData, categories, selectedCategory]);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    const submitData = {
      ...formData,
      name: formData.name.trim(),
      location_id: locationIdValue,
    };
    
    setSubmitting(true);
    
    try {
      if (dialogMode === 'create') {
        await handleCreateCategory(submitData);
      } else if (dialogMode === 'edit' && selectedCategory) {
        await handleUpdateCategory(selectedCategory.id, submitData);
      }
    } catch (err) {
      console.error('[CategoryList] Submit error:', err);
    } finally {
      setSubmitting(false);
    }
  }, [formData, dialogMode, selectedCategory, validateForm, handleCreateCategory, handleUpdateCategory, locationIdValue]);

  // ============================================
  // SEARCH AND FILTER
  // ============================================

  const handleSearch = useCallback((event) => {
    const term = event.target.value.toLowerCase();
    setSearchTerm(term);
    
    if (!term.trim()) {
      setFilteredCategories(categories);
      return;
    }
    
    const filtered = categories.filter(category =>
      category.name?.toLowerCase().includes(term) ||
      category.description?.toLowerCase().includes(term)
    );
    setFilteredCategories(filtered);
    setPage(0);
  }, [categories]);

  const handleClearSearch = useCallback(() => {
    setSearchTerm('');
    setFilteredCategories(categories);
    setPage(0);
  }, [categories]);

  // ============================================
  // SORTING
  // ============================================

  const handleSort = useCallback((property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
    
    const sorted = [...filteredCategories].sort((a, b) => {
      if (property === 'name') {
        return isAsc ? a.name?.localeCompare(b.name) : b.name?.localeCompare(a.name);
      }
      if (property === 'item_count') {
        return isAsc ? (a.item_count || 0) - (b.item_count || 0) : (b.item_count || 0) - (a.item_count || 0);
      }
      if (property === 'created_date') {
        return isAsc ? new Date(a.created_date) - new Date(b.created_date) : new Date(b.created_date) - new Date(a.created_date);
      }
      return 0;
    });
    setFilteredCategories(sorted);
  }, [orderBy, order, filteredCategories]);

  // ============================================
  // SESSION MANAGEMENT - Auto-refresh token
  // ============================================

  useEffect(() => {
    const checkAndRefreshToken = async () => {
      const token = localStorage.getItem('access_token');
      if (!token) return;

      const expiryTime = getTokenExpiryTime(token);
      const timeLeft = expiryTime - Date.now();

      // If less than 2 minutes until expiry, refresh automatically
      if (timeLeft > 0 && timeLeft < 120000) {
        console.log('[CategoryList] Auto-refreshing token...');
        await refreshAuthToken();
      }
    };

    // Check every minute
    const refreshInterval = setInterval(checkAndRefreshToken, 60000);
    return () => clearInterval(refreshInterval);
  }, [refreshAuthToken]);

  // ============================================
  // EFFECTS
  // ============================================

  useEffect(() => {
    console.debug(`[CategoryList] Component mounted for ${locationName}...`);
    fetchCategories();
  }, [fetchCategories, locationName]);

  // ============================================
  // RENDER
  // ============================================

  if (isLoading || isRefreshing) {
    return (
      <Suspense fallback={<div>Loading...</div>}>
        <Preloader text={`Loading ${locationName} Categories...`} size={70} fullScreen={true} />
      </Suspense>
    );
  }

  return (
    <Suspense fallback={<div>Loading...</div>}>
      <div className="epos-wrapper">
        <div className="epos-main-layout">
          <SidebarComponent activeMenu={activeMenu} setActiveMenu={setActiveMenu} />

          <div className="epos-main">
            <Header />
            
            <div className="epos-content">
              {/* Hero Card */}
              <div className="hero-card">
                <div>
                  <div className="hero-title" style={{ color: locationColor, fontWeight: 'bold' }}>
                    {locationConfig.icon} {locationName} Categories
                  </div>
                  <div className="hero-text-wrapper">
                    <div className="hero-tagline">
                      <span className="hero-tagline-icon">✦</span>
                      Manage menu categories
                    </div>
                  </div>
                </div>
                <button 
                  className="new-order-btn" 
                  onClick={() => handleOpenDialog('create')}
                  style={{ backgroundColor: locationColor }}
                >
                  <AddIcon style={{ marginRight: '8px' }} />
                  Add Category
                </button>
              </div>

              {/* Stats Cards */}
              <StatsCard stats={stats} locationColor={locationColor} />

              {/* Search and Actions Bar */}
              <Paper
                sx={{
                  p: 2,
                  mb: 3,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  flexWrap: 'wrap',
                  gap: 2,
                  borderRadius: 2,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1, minWidth: '200px' }}>
                  <TextField
                    size="small"
                    placeholder="Search categories..."
                    value={searchTerm}
                    onChange={handleSearch}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                        </InputAdornment>
                      ),
                      endAdornment: searchTerm && (
                        <InputAdornment position="end">
                          <IconButton size="small" onClick={handleClearSearch}>
                            <ClearIcon fontSize="small" />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{ flex: 1, maxWidth: '400px' }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    {filteredCategories.length} categories found
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Tooltip title="Refresh">
                    <IconButton onClick={fetchCategories} disabled={loading}>
                      <RefreshIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Paper>

              {/* Loading Indicator */}
              {loading && (
                <Box sx={{ width: '100%', mb: 2 }}>
                  <LinearProgress color="primary" />
                </Box>
              )}

              {/* Categories Table */}
              <Paper sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ backgroundColor: alpha(theme.palette.primary.main, 0.04) }}>
                        <TableCell sx={{ fontWeight: 700 }}>#</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>
                          <TableSortLabel
                            active={orderBy === 'name'}
                            direction={orderBy === 'name' ? order : 'asc'}
                            onClick={() => handleSort('name')}
                          >
                            Category Name
                          </TableSortLabel>
                        </TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700 }}>
                          <TableSortLabel
                            active={orderBy === 'item_count'}
                            direction={orderBy === 'item_count' ? order : 'asc'}
                            onClick={() => handleSort('item_count')}
                          >
                            No. of Items
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>
                          <TableSortLabel
                            active={orderBy === 'created_date'}
                            direction={orderBy === 'created_date' ? order : 'asc'}
                            onClick={() => handleSort('created_date')}
                          >
                            Date Added
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700 }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredCategories.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                            <CategoryIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                            <Typography variant="h6" color="textSecondary" gutterBottom>
                              No Categories Found
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                              {searchTerm ? 'Try adjusting your search terms' : 'Start by adding your first category'}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        filteredCategories
                          .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                          .map((category, index) => (
                            <CategoryRow
                              key={category.id}
                              category={category}
                              index={page * rowsPerPage + index}
                              onView={handleOpenDialog}
                              onEdit={handleOpenDialog}
                              onToggle={handleOpenToggleDialog}
                              onDelete={handleOpenDeleteDialog}
                            />
                          ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                
                {/* Pagination */}
                {filteredCategories.length > rowsPerPage && (
                  <TablePagination
                    component="div"
                    count={filteredCategories.length}
                    page={page}
                    onPageChange={(_, newPage) => setPage(newPage)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => {
                      setRowsPerPage(parseInt(e.target.value, 10));
                      setPage(0);
                    }}
                    rowsPerPageOptions={[5, 10, 25, 50]}
                  />
                )}
              </Paper>
            </div>
          </div>
        </div>
        <Footer />
      </div>

      {/* Create/Edit/View Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        TransitionComponent={Fade}
        transitionDuration={200}
        PaperProps={{
          sx: {
            borderRadius: 2,
            maxHeight: '90vh',
          },
        }}
      >
        <form onSubmit={handleSubmit}>
          <DialogTitle
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {dialogMode === 'create' && <AddIcon color="primary" />}
              {dialogMode === 'edit' && <EditIcon color="info" />}
              {dialogMode === 'view' && <ViewIcon color="primary" />}
              <Typography variant="h6" fontWeight="bold">
                {dialogMode === 'create' && `Create Category - ${locationName}`}
                {dialogMode === 'edit' && `Edit Category - ${locationName}`}
                {dialogMode === 'view' && `Category Details - ${locationName}`}
              </Typography>
            </Box>
            <IconButton onClick={handleCloseDialog} size="small">
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          
          <DialogContent dividers sx={{ pt: 3 }}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Category Name"
                  value={formData?.name || ''}
                  onChange={(e) => handleFormChange('name', e.target.value)}
                  error={!!formErrors.name}
                  helperText={formErrors.name}
                  disabled={dialogMode === 'view'}
                  required
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <CategoryIcon fontSize="small" color="action" />
                      </InputAdornment>
                    ),
                  }}
                  autoFocus={dialogMode !== 'view'}
                  placeholder="e.g., Appetizers, Main Course, Desserts"
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  value={formData?.description || ''}
                  onChange={(e) => handleFormChange('description', e.target.value)}
                  disabled={dialogMode === 'view'}
                  multiline
                  rows={3}
                  placeholder="Brief description of the category"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start" sx={{ alignItems: 'flex-start', pt: 1.5 }}>
                        <MenuIcon fontSize="small" color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth disabled={dialogMode === 'view'}>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={formData?.is_active ? 'active' : 'inactive'}
                    onChange={(e) => handleFormChange('is_active', e.target.value === 'active')}
                    label="Status"
                  >
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="inactive">Inactive</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Created Date"
                  type="date"
                  value={formData?.created_date || ''}
                  onChange={(e) => handleFormChange('created_date', e.target.value)}
                  error={!!formErrors.created_date}
                  helperText={formErrors.created_date}
                  disabled={dialogMode === 'view'}
                  required
                  InputLabelProps={{
                    shrink: true,
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <CalendarIcon fontSize="small" color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>
          </DialogContent>
          
          <DialogActions sx={{ p: 2, gap: 1 }}>
            <Button onClick={handleCloseDialog} color="inherit">
              {dialogMode === 'view' ? 'Close' : 'Cancel'}
            </Button>
            {dialogMode !== 'view' && (
              <Button
                type="submit"
                variant="contained"
                color="primary"
                startIcon={dialogMode === 'create' ? <SaveIcon /> : <SaveIcon />}
                disabled={submitting}
                sx={{
                  backgroundColor: locationColor,
                  '&:hover': {
                    backgroundColor: locationColor,
                    opacity: 0.9,
                  },
                }}
              >
                {submitting ? <CircularProgress size={24} color="inherit" /> : (dialogMode === 'create' ? 'Create Category' : 'Update Category')}
              </Button>
            )}
          </DialogActions>
        </form>
      </Dialog>

      {/* Toggle (Activate/Deactivate) Confirmation Dialog */}
      <Dialog
        open={toggleDialogOpen}
        onClose={handleCloseToggleDialog}
        TransitionComponent={Fade}
        transitionDuration={200}
        PaperProps={{
          sx: {
            borderRadius: 2,
            p: 1,
            maxWidth: '500px',
          },
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {categoryToToggle?.is_active ? (
            <CancelIcon color="warning" />
          ) : (
            <CheckCircleIcon color="success" />
          )}
          <Typography variant="h6" fontWeight="bold">
            {categoryToToggle?.is_active ? 'Deactivate' : 'Activate'} Category
          </Typography>
        </DialogTitle>
        
        <DialogContent>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
            <InfoIcon color="info" sx={{ mt: 0.5 }} />
            <Typography variant="body1">
              Are you sure you want to <strong>{categoryToToggle?.is_active ? 'deactivate' : 'activate'}</strong> category <strong>"{categoryToToggle?.name}"</strong>?
            </Typography>
          </Box>
          
          <Alert severity={categoryToToggle?.is_active ? 'warning' : 'info'} sx={{ mt: 2 }}>
            {categoryToToggle?.is_active 
              ? 'Deactivating will hide this category from the menu. Items in this category will not be displayed to customers.'
              : 'Activating will make this category visible in the menu again. All items in this category will be displayed.'}
          </Alert>
          
          {categoryToToggle?.item_count > 0 && categoryToToggle?.is_active && (
            <Alert severity="info" sx={{ mt: 2 }}>
              This category contains <strong>{categoryToToggle.item_count}</strong> items. Deactivating will hide all items in this category.
            </Alert>
          )}
        </DialogContent>
        
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={handleCloseToggleDialog} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={() => handleToggleCategoryStatus(categoryToToggle?.id, categoryToToggle?.is_active)}
            variant="contained"
            color={categoryToToggle?.is_active ? 'warning' : 'success'}
            startIcon={categoryToToggle?.is_active ? <CancelIcon /> : <CheckCircleIcon />}
            disabled={toggling}
          >
            {toggling ? <CircularProgress size={24} /> : (categoryToToggle?.is_active ? 'Deactivate' : 'Activate')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Permanent Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        TransitionComponent={Fade}
        transitionDuration={200}
        PaperProps={{
          sx: {
            borderRadius: 2,
            p: 1,
            maxWidth: '550px',
          },
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <DeleteIcon color="error" />
          <Typography variant="h6" fontWeight="bold" color="error">
            Permanent Delete
          </Typography>
        </DialogTitle>
        
        <DialogContent>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 3 }}>
            <WarningIcon color="error" sx={{ mt: 0.5, fontSize: 32 }} />
            <Box>
              <Typography variant="body1" gutterBottom>
                You are about to <strong style={{ color: '#d32f2f' }}>permanently delete</strong> category <strong>"{categoryToDelete?.name}"</strong>.
              </Typography>
              <Typography variant="body2" color="textSecondary">
                This action <strong style={{ color: '#d32f2f' }}>cannot be undone</strong> and will permanently remove this category from the system.
              </Typography>
            </Box>
          </Box>
          
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>Warning:</strong> Deleting this category will remove it from all menu items associated with it.
              {categoryToDelete?.item_count > 0 && (
                <span> This category currently has <strong>{categoryToDelete.item_count}</strong> items that will be affected.</span>
              )}
            </Typography>
          </Alert>
          
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" gutterBottom>
              To confirm deletion, type the category name <strong>"{categoryToDelete?.name}"</strong> below:
            </Typography>
            <TextField
              fullWidth
              size="small"
              placeholder={`Type "${categoryToDelete?.name}" to confirm`}
              value={deleteConfirmation}
              onChange={(e) => setDeleteConfirmation(e.target.value)}
              error={deleteConfirmation !== '' && deleteConfirmation !== categoryToDelete?.name}
              helperText={deleteConfirmation !== '' && deleteConfirmation !== categoryToDelete?.name ? 'Category name does not match' : ''}
              sx={{ mt: 1 }}
              autoFocus
            />
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={handleCloseDeleteDialog} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={() => handlePermanentDeleteCategory(categoryToDelete?.id)}
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
            disabled={deleting || deleteConfirmation !== categoryToDelete?.name}
            sx={{
              '&:disabled': {
                opacity: 0.5,
              },
            }}
          >
            {deleting ? <CircularProgress size={24} color="inherit" /> : 'Permanently Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar Notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        TransitionComponent={Fade}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Floating Action Button for Mobile */}
      <Fab
        color="primary"
        aria-label="add category"
        sx={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          display: { xs: 'flex', sm: 'none' },
          zIndex: 1000,
          backgroundColor: locationColor,
          '&:hover': {
            backgroundColor: locationColor,
            opacity: 0.8,
          },
        }}
        onClick={() => handleOpenDialog('create')}
      >
        <AddIcon />
      </Fab>
    </Suspense>
  );
};

export default CategoryList;